import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const Lightbox = ({ images, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDirection, setSlideDirection] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  // Prevent body scroll when lightbox is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && !isAnimating) goToPrevious();
      if (e.key === 'ArrowRight' && !isAnimating) goToNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, onClose, isAnimating]);

  // Preload adjacent images for smooth transitions
  useEffect(() => {
    const preloadImage = (index) => {
      if (images[index]) {
        const img = new Image();
        img.src = images[index];
      }
    };

    // Preload next and previous images
    const nextIndex = (currentIndex + 1) % images.length;
    const prevIndex = (currentIndex - 1 + images.length) % images.length;
    preloadImage(nextIndex);
    preloadImage(prevIndex);
  }, [currentIndex, images]);

  // Reset image loaded state when index changes
  useEffect(() => {
    setImageLoaded(false);
  }, [currentIndex]);

  const goToNext = () => {
    if (isAnimating || images.length <= 1) return;
    setIsAnimating(true);
    setSlideDirection('left');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setSlideDirection(null);
      setIsAnimating(false);
    }, 300);
  };

  const goToPrevious = () => {
    if (isAnimating || images.length <= 1) return;
    setIsAnimating(true);
    setSlideDirection('right');
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
      setSlideDirection(null);
      setIsAnimating(false);
    }, 300);
  };

  // Touch handlers for swipe gestures
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  if (!images || images.length === 0) return null;

  const getImageAnimationClass = () => {
    if (!imageLoaded) return 'opacity-0 scale-95';
    if (slideDirection === 'left') return 'opacity-0 -translate-x-20';
    if (slideDirection === 'right') return 'opacity-0 translate-x-20';
    return 'opacity-100 translate-x-0 scale-100';
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/97 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
      onClick={onClose}
    >
      {/* Close Button - Mobile optimized */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 md:top-6 md:right-6 text-white/90 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 md:p-3 transition-all duration-300 z-50 backdrop-blur-sm"
        aria-label="Close lightbox"
      >
        <FaTimes size={24} className="md:w-7 md:h-7" />
      </button>

      {/* Image Counter - Mobile optimized */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 text-white font-body text-sm md:text-lg z-50 bg-black/30 backdrop-blur-sm px-3 py-2 rounded-full">
        <span className="font-semibold">{currentIndex + 1}</span>
        <span className="text-white/70 mx-1">/</span>
        <span className="text-white/70">{images.length}</span>
      </div>

      {/* Navigation Buttons - Desktop only */}
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => {
              e.stopPropagation();
              goToPrevious();
            }}
            disabled={isAnimating}
            className="hidden md:flex absolute left-4 lg:left-8 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-4 transition-all duration-300 z-50 backdrop-blur-sm disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Previous image"
          >
            <FaChevronLeft size={28} />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              goToNext();
            }}
            disabled={isAnimating}
            className="hidden md:flex absolute right-4 lg:right-8 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-4 transition-all duration-300 z-50 backdrop-blur-sm disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Next image"
          >
            <FaChevronRight size={28} />
          </button>
        </>
      )}

      {/* Image Container with touch gestures */}
      <div
        className="w-full h-full flex items-center justify-center p-4 md:p-8"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          ref={imageRef}
          src={images[currentIndex]}
          alt={`Portfolio item ${currentIndex + 1} of ${images.length}`}
          className={`max-w-full max-h-full object-contain transition-all duration-500 ease-out ${getImageAnimationClass()}`}
          onLoad={() => setImageLoaded(true)}
          style={{ maxHeight: 'calc(100vh - 8rem)' }}
        />
      </div>

      {/* Swipe indicator for mobile */}
      {images.length > 1 && (
        <div className="md:hidden absolute bottom-20 left-1/2 transform -translate-x-1/2 text-white/60 text-xs font-body flex items-center gap-2 bg-black/30 backdrop-blur-sm px-4 py-2 rounded-full">
          <FaChevronLeft size={12} />
          <span>Swipe to navigate</span>
          <FaChevronRight size={12} />
        </div>
      )}

      {/* Thumbnail Navigation - Desktop only */}
      {images.length > 1 && images.length <= 10 && (
        <div className="hidden md:flex absolute bottom-6 lg:bottom-8 left-1/2 transform -translate-x-1/2 gap-3 overflow-x-auto max-w-screen-lg px-4 scrollbar-hide">
          {images.map((image, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                if (!isAnimating) {
                  setCurrentIndex(index);
                }
              }}
              disabled={isAnimating}
              className={`flex-shrink-0 w-16 h-16 lg:w-20 lg:h-20 rounded-lg overflow-hidden transition-all duration-400 ${
                index === currentIndex
                  ? 'ring-2 ring-accent opacity-100 scale-110'
                  : 'opacity-50 hover:opacity-80 hover:scale-105'
              } ${isAnimating ? 'cursor-not-allowed' : 'cursor-pointer'}`}
            >
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {/* Mobile navigation dots for many images */}
      {images.length > 10 && (
        <div className="md:hidden absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-accent w-6'
                  : 'bg-white/40'
              }`}
              aria-label={`Go to image ${index + 1}`}
            />
          ))}
        </div>
      )}

      {/* Loading indicator */}
      {!imageLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-white/20 border-t-accent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
};

export default Lightbox;
